/**
 * /about page i18n catalog.
 *
 * Source: specs/GTM/GNPC-APPLICATION-KIT.md §3.1 (VN authoritative draft).
 * EN translation inline by builder (bilingual). Single-locale render via
 * useLang() per legal-upl single-locale disclaimer rule.
 */

import type { Lang } from '../use-lang';

export interface AboutCopy {
  meta_title: string;
  meta_description: string;
  page_title: string;
  intro: string;

  mission_heading: string;
  mission_body: string;

  not_heading: string;
  not_item_1: string;
  not_item_2: string;
  not_item_3: string;

  is_heading: string;
  is_item_1: string;
  is_item_2: string;
  is_item_3: string;

  compliance_heading: string;
  compliance_intro: string;
  compliance_ai: string;
  compliance_pdpa: string;
  compliance_upl: string;
  compliance_press: string;

  contact_heading: string;
  contact_email: string;
  contact_dpo: string;
  contact_legal: string;
  contact_transparency_link: string;
  contact_editorial_link: string;
}

export const ABOUT_VI: AboutCopy = {
  meta_title: 'Về Asklaw — AI legal copilot Việt Nam',
  meta_description:
    'Asklaw là AI legal copilot cho người dân Việt Nam, xây dựng trên kho VBPL chính thống Bộ Tư pháp 166,000 văn bản.',
  page_title: 'Về Asklaw',
  intro:
    'Asklaw là AI legal copilot cho người dân Việt Nam, được xây dựng trên hạ tầng pháp luật chính thống — kho VBPL của Bộ Tư pháp với 166,000 văn bản quy phạm pháp luật.',

  mission_heading: 'Sứ mệnh',
  mission_body:
    'Pháp luật Việt Nam phức tạp và thay đổi liên tục. Người dân thường mất hàng giờ Google để tìm văn bản gốc của một nghị định được báo nhắc tới. Asklaw làm việc đó giùm bạn — đọc các nguồn tin chính thống mỗi sáng, tóm tắt và liên kết với Điều/Khoản gốc.',

  not_heading: 'Asklaw KHÔNG phải gì',
  not_item_1:
    'Asklaw KHÔNG phải luật sư. Chúng tôi không có chứng chỉ hành nghề luật.',
  not_item_2:
    'Asklaw KHÔNG đưa tư vấn pháp lý cho trường hợp cụ thể của bạn. Đây là vai trò của luật sư.',
  not_item_3:
    'Asklaw KHÔNG phải tòa án, không phải cơ quan nhà nước, không thể đại diện bạn trong vụ việc.',

  is_heading: 'Asklaw LÀ gì',
  is_item_1:
    'Công cụ AI tham khảo giúp bạn hiểu pháp luật nhanh hơn 30 phút mỗi ngày.',
  is_item_2:
    'Cổng tổng hợp tin pháp luật từ các nguồn chính thống, có trích dẫn Điều/Khoản.',
  is_item_3:
    'Trợ lý tra cứu văn bản pháp luật miễn phí, dựa trên kho VBPL chính thống.',

  compliance_heading: 'Tuân thủ pháp luật',
  compliance_intro: 'Asklaw được thiết kế theo:',
  compliance_ai: 'Luật AI 2026 (hiệu lực 01/03/2026) — minh bạch AI',
  compliance_pdpa:
    'Luật Bảo vệ dữ liệu cá nhân 2026 (hiệu lực 01/09/2026) — bảo vệ dữ liệu người dùng',
  compliance_upl: 'Luật Luật sư + Quy định UPL — không thay thế luật sư',
  compliance_press:
    'Luật Sở hữu trí tuệ 2005 Điều 25 + Luật Báo chí 2016 Điều 38 — trích dẫn báo chí hợp lý',

  contact_heading: 'Liên hệ',
  contact_email: 'Email: hello@asklaw.vn',
  contact_dpo: 'DPO (bảo vệ dữ liệu): dpo@asklaw.vn',
  contact_legal: 'Pháp lý + takedown: legal@asklaw.vn',
  contact_transparency_link: 'Trang minh bạch',
  contact_editorial_link: 'Tiêu chuẩn biên tập',
};

export const ABOUT_EN: AboutCopy = {
  meta_title: 'About Asklaw — AI legal copilot for Vietnam',
  meta_description:
    'Asklaw is an AI legal copilot for Vietnam, built on the official Vietnam legal corpus of 166,000 documents from the Ministry of Justice.',
  page_title: 'About Asklaw',
  intro:
    'Asklaw is an AI legal copilot for Vietnam, built on the official legal infrastructure — the Ministry of Justice corpus of 166,000 normative legal documents.',

  mission_heading: 'Mission',
  mission_body:
    "Vietnamese law is complex and constantly changing. Citizens often spend hours googling to find the original decree that a news article references. Asklaw does that work for you — reading official sources every morning, summarising, and linking to the original Article/Clause.",

  not_heading: 'What Asklaw is NOT',
  not_item_1:
    'Asklaw is NOT a law firm. We hold no legal practice certificate.',
  not_item_2:
    'Asklaw does NOT give legal advice for your specific case. That role belongs to a licensed lawyer.',
  not_item_3:
    'Asklaw is NOT a court, NOT a government agency, and cannot represent you in proceedings.',

  is_heading: 'What Asklaw IS',
  is_item_1:
    'An AI reference tool that helps you understand the law 30 minutes faster every day.',
  is_item_2:
    'An aggregator of legal news from trusted sources, with Article/Clause citations.',
  is_item_3:
    'A free legal-document lookup assistant, grounded in the official VBPL corpus.',

  compliance_heading: 'Legal compliance',
  compliance_intro: 'Asklaw is designed to comply with:',
  compliance_ai: 'AI Law 2026 (effective 01/03/2026) — AI transparency',
  compliance_pdpa:
    'Personal Data Protection Law 2026 (effective 01/09/2026) — user data protection',
  compliance_upl:
    'Lawyer Law + UPL regulation — Asklaw does not replace a lawyer',
  compliance_press:
    'IP Law 2005 Article 25 + Press Law 2016 Article 38 — fair-use journalistic citation',

  contact_heading: 'Contact',
  contact_email: 'Email: hello@asklaw.vn',
  contact_dpo: 'DPO (data protection): dpo@asklaw.vn',
  contact_legal: 'Legal + takedown: legal@asklaw.vn',
  contact_transparency_link: 'Transparency page',
  contact_editorial_link: 'Editorial standards',
};

export function resolveAbout(lang: Lang): AboutCopy {
  return lang === 'en' ? ABOUT_EN : ABOUT_VI;
}
